import { memo, useCallback, useState, useEffect } from 'react';
import { SavedViewModelType } from 'src/api/generated';
import type { ComponentSize } from 'src/modules/shared/types/component.type';
import type {
  SavedView,
  SavedViewConfig,
  SearcherDocumentSearchFilter,
  SearcherDocumentSearchSort,
} from 'src/api/generated';
import { SavedViewDropdown } from '../saved-view-dropdown';
import { SavedViewModal } from '../saved-view-modal';
import { useSavedViewContext } from '../../../../context/saved-view.context';
import type {
  Filter,
  FilterFieldOptions,
  Sort,
} from 'src/modules/shared/types/filters.types';
import { fromApiFilter } from 'src/modules/shared/utils/filters/mapper.util';
import { toApiFilter } from 'src/modules/shared/utils/filters/mapper.util';
import { useCurrentUser } from 'src/modules/shared/hooks/api/admin/users';

interface SavedViewDropdownWrapperProps {
  className?: string;
  dropdownClassName?: string;
  handlerContainerClassName?: string;
  size?: ComponentSize;
  filters: Filter[];
  setFilters: (filters: Filter[]) => void;
  sorts: Sort[];
  setSorts: (sorts: Sort[]) => void;
  fieldsOptions?: FilterFieldOptions[];
  resetFilters: () => void;
  resetSorts: () => void;
  compactActions?: boolean;
}

export const SavedViewDropdownWrapper = memo<SavedViewDropdownWrapperProps>(
  ({
    className = '',
    dropdownClassName = '',
    handlerContainerClassName = '',
    size = 'md',
    filters,
    setFilters,
    sorts,
    setSorts,
    fieldsOptions,
    resetFilters,
    resetSorts,
    compactActions = false,
  }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedViewForEdit, setSelectedViewForEdit] = useState<
      SavedView | undefined
    >();

    const { selectedView, setSelectedView, deleteView } = useSavedViewContext();

    const { user } = useCurrentUser();

    const handleViewCreate = useCallback(() => {
      setSelectedViewForEdit(undefined);
      setIsModalOpen(true);
    }, []);

    const handleViewEdit = useCallback((view: SavedView) => {
      setSelectedViewForEdit(view);
      setIsModalOpen(true);
    }, []);

    const applyViewConfig = useCallback(
      (viewConfig: SavedViewConfig) => {
        const uiFilters: Filter[] = viewConfig.filters.map((filter, index) =>
          fromApiFilter(filter, `filter-${Date.now()}-${index}`)
        );
        const uiSorts: Sort[] = viewConfig.sorts.map((sort, index) => ({
          id: `sort-${Date.now()}-${index}`,
          fieldOptionId: sort.field,
          direction: sort.direction,
        }));

        setFilters(uiFilters);
        setSorts(uiSorts);
      },
      [setFilters, setSorts]
    );

    const handleViewSelect = useCallback(
      (view: SavedView) => {
        if (view.id === -1) {
          setSelectedView(null);
          resetFilters();
          resetSorts();
          return;
        }

        applyViewConfig(view.config);
        setSelectedView(view);
      },
      [applyViewConfig, setSelectedView, resetFilters, resetSorts]
    );

    useEffect(() => {
      if (!selectedView) {
        return;
      }

      applyViewConfig(selectedView.config);
    }, [applyViewConfig, selectedView]);

    const handleModalClose = useCallback(() => {
      setIsModalOpen(false);
      setSelectedViewForEdit(undefined);
    }, []);

    const handleModalSuccess = useCallback(
      (_updatedView: SavedView) => {
        handleModalClose();
      },
      [handleModalClose]
    );

    const getCurrentConfig = useCallback(() => {
      const currentFilters: SearcherDocumentSearchFilter[] = filters.flatMap(
        (filter) => toApiFilter(filter, fieldsOptions)
      );
      const currentSorts: SearcherDocumentSearchSort[] = sorts.map((sort) => ({
        field: sort.fieldOptionId,
        direction: sort.direction,
      }));

      return {
        filters: currentFilters,
        sorts: currentSorts,
      };
    }, [fieldsOptions, filters, sorts]);

    const handleViewDelete = useCallback(
      async (view: SavedView) => {
        if (!user || view.userId !== user.id) return;

        try {
          await deleteView(view.id);

          if (selectedView && selectedView.id === view.id) {
            setSelectedView(null);
          }
        } catch (error) {
          console.error('Failed to delete view:', error);
        }
      },
      [deleteView, selectedView, setSelectedView, user]
    );

    return (
      <div className="flex items-center gap-2">
        <SavedViewDropdown
          className={className}
          dropdownClassName={dropdownClassName}
          handlerContainerClassName={handlerContainerClassName}
          model={SavedViewModelType.CONVERSATION}
          selectedViewId={selectedView?.id}
          onViewCreate={handleViewCreate}
          onViewEdit={handleViewEdit}
          onViewSelect={handleViewSelect}
          onViewDelete={handleViewDelete}
          size={size}
          compactActions={compactActions}
        />

        <SavedViewModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          model={SavedViewModelType.CONVERSATION}
          view={selectedViewForEdit}
          config={getCurrentConfig()}
          onSuccess={handleModalSuccess}
        />
      </div>
    );
  }
);

SavedViewDropdownWrapper.displayName = 'SavedViewDropdownWrapper';
